import clsx from 'clsx';
import { useToggle } from 'react-use';

import EditableFieldRawHtml from '~components/generic/editable-field/components/EditableFieldRawHtml';
import EditableField from '~components/generic/editable-field/EditableField/EditableField';
import { IEntityComponentProps } from '~interfaces/props';
import CodeBlock from '~modules/ui-component/CodeBlock';
import CodePenButton from '~modules/ui-component/CodePenButton';
import CopyToClipboardButton from '~modules/ui-component/CopyToClipboardButton';
import GitHubButton from '~modules/ui-component/GitHubButton';
import PreviewStage from '~modules/ui-component/PreviewStage';
import ToggleCodeButton from '~modules/ui-component/ToggleCodeButton';

export interface IModuleUiComponentState {
  showCode: boolean;
}

export default function ModuleUiComponent(props: IEntityComponentProps) {
  const [showCode, toggleShowCode] = useToggle(props.data.attributes.field_show_code_by_default || false);
  const { data, editing, className, onChange, origData } = props;
  const content = data.attributes;
  const moduleClasses = clsx({ ...className.module }, 'relative', 'c-ui-component');
  const containerClasses = clsx({ ...className.container }, 'px-0', 'relative');

  const layout = content.field_layout || 1;
  const layoutContainerClasses = clsx('row', 'flex-wrap', 'c-module__content', {
    'flex-row-reverse': layout === 3,
  });
  const layoutPreviewClasses = clsx({
    col: true,
    'col-12': layout === 1,
    'col-6': layout !== 1,
    'u-margin-top-md': layout === 1,
  });
  const layoutDescriptionClasses = clsx({
    col: true,
    'col-12': layout === 1,
    'col-6': layout !== 1,
    'o-field-text': true,
  });

  const showTitle = editing || Boolean(content.field_title.value);
  const showActionButtons = !editing;
  const showCodeEdit = editing;
  const showCodeBlock = !editing && showCode;

  return (
    <div className={moduleClasses} id={origData}>
      <div className={containerClasses}>
        <div className="row c-module__header">
          {showTitle && (
            <div className="col col-12">
              <EditableField
                hide={content.field_hide_title}
                entityType={data.type}
                fieldName="attributes.field_title.value"
                data={content.field_title.value}
                editing={editing}
                onChange={onChange}
              />
            </div>
          )}
        </div>
        <div className={layoutContainerClasses}>
          <div className={layoutDescriptionClasses}>
            <EditableField
              hide={content.field_hide_text}
              entityType={data.type}
              fieldName="attributes.field_text.value"
              data={content.field_text.value}
              editing={editing}
              onChange={onChange}
            />
          </div>
          <div className={layoutPreviewClasses}>
            <PreviewStage code={content.field_html_source_code} />
          </div>
        </div>
        <div className="row flex-wrap c-module__content">
          {showActionButtons && (
            <div className="col col-12 u-margin-top-xs">
              <div className="row no-gutters justify-content-end">
                <ToggleCodeButton onClick={toggleShowCode} isOn={showCode} />
                <CopyToClipboardButton code={content.field_html_source_code} />
                <GitHubButton link={content.field_link_github} />
                <CodePenButton link={content.field_link_codepen} />
              </div>
            </div>
          )}
          <div className="col col-12">
            {showCodeEdit && (
              <div className="u-margin-top-md">
                <EditableFieldRawHtml
                  entityType={data.type}
                  fieldName="attributes.field_html_source_code"
                  data={content.field_html_source_code}
                  editing={editing}
                  onChange={onChange}
                />
              </div>
            )}
            {showCodeBlock && <CodeBlock code={content.field_html_source_code} />}
          </div>
        </div>
      </div>
    </div>
  );
}
